/**
 * WHAT A NEW ROOM CAN BE ASKED, in one place.
 *
 * Each question is a line of the room's first message and the table that
 * answers it. Ticking one says it to the chat AND puts its table on the board,
 * so the chat does not have to guess the table from the wording. Add a
 * question here and the start form offers it. Pure.
 */

export interface StartQuestion {
  key: string;
  /** As the form shows it. */
  label: string;
  /** As the first message says it, after the main question. */
  ask: string;
  /** The catalogue table that answers it (`jcseo/boardroom/deliverables.py`). */
  table: string;
}

/** "Who sells" or "who competes with" comes first; these follow it. */
export const START_QUESTIONS: StartQuestion[] = [
  { key: 'price', label: 'What it costs', ask: 'what it costs', table: 'price_map' },
  { key: 'pain', label: 'What people complain about', ask: 'what people complain about', table: 'pain_gap' },
  { key: 'search', label: 'What people search for', ask: 'what people search for', table: 'search_market' },
  { key: 'ranks', label: 'Who shows up first in search', ask: 'who shows up first in search', table: 'who_ranks' },
  {
    key: 'appeal',
    label: 'Who buys it, and how it is pitched',
    ask: 'who buys it and how it is pitched',
    table: 'appeal',
  },
  {
    key: 'openings',
    label: 'Where there is room for something new',
    ask: 'where there is room for something new',
    table: 'opportunity_brief',
  },
];

/** The main question's table: who the players are. */
export const PLAYERS_TABLE = 'competitors';

export type CustomerSize = 'any' | 'small' | 'large';

export const SIZE_WORDS: Record<CustomerSize, string> = {
  any: 'Any',
  small: 'Small businesses',
  large: 'Large companies',
};

/** Why they want to know. The room's chat asks this first when it is not said. */
export const PURPOSES = [
  { key: 'buy', label: 'Choosing one to buy', says: "I'm choosing one to buy." },
  { key: 'sell', label: 'Selling into this market', says: 'I want to sell into this market.' },
  { key: 'build', label: 'Building something to compete', says: "I'm building something to compete." },
  { key: 'write', label: 'Writing about it', says: "I'm writing about it." },
] as const;

export type Purpose = (typeof PURPOSES)[number]['key'] | '';

export interface StartAnswers {
  /** A thing ("practice software") or a company's web address. */
  about: string;
  /** Keys of `START_QUESTIONS` ticked. */
  picked: string[];
  /** Who it is for: "mental health practices". */
  audience: string;
  /** Where: "the United States". */
  where: string;
  size: CustomerSize;
  /** "" when not said. */
  why?: Purpose;
}

export interface CompanyStart {
  url: string;
  domain: string;
}

/** A typed web address as a company. Null when it is not one: a thing with
 *  no dot in it is a thing ("practice software"). Pure. */
export function companyStart(raw: string): CompanyStart | null {
  const text = raw.trim();
  if (!text || /\s/.test(text)) return null;
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  const domain = parsed.hostname.toLowerCase().replace(/^www\./, '');
  if (!/\.[a-z]{2,}$/.test(domain)) return null;
  return { url: parsed.origin, domain };
}

export interface StartPlan {
  /** The room's first chat message. */
  line: string;
  /** Tables to put on the board, in order, no repeats. */
  tables: string[];
  /** The company to add first, when the room starts from one. */
  company: CompanyStart | null;
}

/** "a, b and c". */
function listed(parts: string[]): string {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The answers as one first message and the tables to start with. Null until
 *  something is typed in the "about" box. Pure. */
export function startPlan(a: StartAnswers): StartPlan | null {
  const about = a.about.trim().replace(/[?.!]+$/, '');
  if (!about) return null;
  const company = companyStart(about);
  const sized = a.size === 'any' ? '' : SIZE_WORDS[a.size].toLowerCase();
  const who = a.audience.trim();
  // "for mental health practices (small businesses)", not "for ... for ...".
  const forWhom = who && sized ? `for ${who} (${sized})` : who || sized ? `for ${who || sized}` : '';
  const narrow = [forWhom, a.where.trim() && `in ${a.where.trim()}`].filter(Boolean) as string[];
  const main = company ? `Who competes with ${company.domain}` : `Who sells ${about}`;
  const picked = START_QUESTIONS.filter(q => a.picked.includes(q.key));
  const first = [main, ...narrow].join(' ');
  const asked = picked.length ? `${first}? Also: ${listed(picked.map(q => q.ask))}.` : `${first}?`;
  const why = PURPOSES.find(p => p.key === a.why);
  const line = why ? `${asked} ${why.says}` : asked;
  const tables = Array.from(new Set([PLAYERS_TABLE, ...picked.map(q => q.table)]));
  return { line, tables, company };
}

// ------------------------------------------------------- a story, a person, a trend

/** What a researcher writing about a story asks, each with the table that
 *  answers it (`jcseo/boardroom/story.py` and friends). All ticked to start. */
export const STORY_QUESTIONS: StartQuestion[] = [
  { key: 'story', label: 'What happened, and when', ask: 'what happened and when', table: 'story' },
  { key: 'people', label: 'Who is in it, and what they said', ask: 'who is in it and what they said', table: 'people' },
  { key: 'says', label: 'What people are saying', ask: 'what people are saying about it', table: 'pain_gap' },
  { key: 'search', label: 'What people search about it', ask: 'what people search about it', table: 'search_market' },
  { key: 'covers', label: 'Who already covers it', ask: 'who already covers it', table: 'who_ranks' },
  { key: 'angles', label: 'What nobody has written yet', ask: 'what nobody has written yet', table: 'angles' },
];

export const STORY_DEFAULT_PICKS = STORY_QUESTIONS.map(q => q.key);

/** A story's first message and tables. Null until something is typed. Pure. */
export function storyPlan(about: string, picked: string[]): { line: string; tables: string[] } | null {
  const what = about.trim().replace(/[?.!]+$/, '');
  if (!what) return null;
  const qs = STORY_QUESTIONS.filter(q => picked.includes(q.key));
  const asks = qs.length ? `: ${listed(qs.map(q => q.ask))}` : '';
  return {
    line: `What is the story behind ${what}${asks}?`,
    tables: Array.from(new Set(qs.map(q => q.table))),
  };
}
